import React from 'react';
import type {
  ToolResultBlock,
  ToolUseBlock,
} from '../../../../store/chatStore';
import type { DetailsConfig, Field } from './types';
import { GroupRenderer } from './components/GroupRenderer';
// import { matchesDetailsConfig } from './types';
import { processTemplate } from './utils';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
interface DetailsRendererProps {
  result: ToolResultBlock;
  toolUse: ToolUseBlock;
}

// Helper function to safely parse JSON
const safeJSONParse = (content: unknown): unknown => {
  if (typeof content !== 'string') {
    return content;
  }

  try {
    return JSON.parse(content);
  } catch (e) {
    return content;
  }
};

// Helper function to check if content is a record
function isRecord(content: unknown): content is Record<string, unknown> {
  return (
    typeof content === 'object' && content !== null && !Array.isArray(content)
  );
}

// Helper function to create a default configuration from data
function createDefaultConfig(
  t: TFunction<'pleri/chat'>,
  data: Record<string, unknown>,
): DetailsConfig {
  const fields: Field[] = Object.entries(data).map(([key, value]) => {
    let type: Field['type'] = 'text';
    let isExpandable = false;

    // Determine field type based on value
    if (typeof value === 'number') {
      type = 'number';
    } else if (value instanceof Date) {
      type = 'datetime';
    } else if (Array.isArray(value)) {
      type = 'list';
      isExpandable = value.length > 5; // Make expandable if array is long
    } else if (typeof value === 'object' && value !== null) {
      type = 'json';
      isExpandable = true;
    }

    return {
      field: key,
      label:
        key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
      type,
      isExpandable,
      maxLength: Array.isArray(value) ? 5 : undefined, // Show first 5 items by default for arrays
    };
  });

  return {
    title: t('common:details'),
    groups: [
      {
        title: t('information'),
        layout: 'column',
        fields,
      },
    ],
    showLabels: true,
    showEmptyFields: false,
    showGroupTitles: true,
    showGroupDescriptions: true,
  };
}

export const DetailsRenderer: React.FC<DetailsRendererProps> = ({
  result,
  toolUse,
}) => {
  const { t } = useTranslation('pleri/chat');
  // Get the visualization config from toolUse meta
  const config = (
    toolUse.meta as { visualization?: { primaryVisualization: DetailsConfig } }
  )?.visualization?.primaryVisualization;

  // Parse the content first
  const parsedContent = safeJSONParse(result.content);

  // Check if parsed content is a valid record with data property
  if (
    !parsedContent ||
    !isRecord(parsedContent) ||
    !('data' in parsedContent)
  ) {
    return null;
  }

  const content = (parsedContent as { data: Record<string, unknown> }).data;

  // Use provided config or create a default one
  const renderConfig = config || createDefaultConfig(t, content);

  // Process templates in title and description
  const title = renderConfig.title
    ? processTemplate(renderConfig.title, content)
    : undefined;
  const description = renderConfig.description
    ? processTemplate(renderConfig.description, content)
    : undefined;

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900 ${
        renderConfig.containerClassName || ''
      }`}
      style={renderConfig.containerStyle}
    >
      {title && (
        <div className="mb-4">
          <h2 className="text-xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
            <ChatMarkdown content={title} />
          </h2>
          {description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              <ChatMarkdown content={description} />
            </p>
          )}
        </div>
      )}

      <div className="space-y-6">
        {renderConfig.groups.map((group, index) => (
          <GroupRenderer
            key={`group-${index}`}
            group={group}
            data={content}
            showLabels={renderConfig.showLabels}
            showEmptyFields={renderConfig.showEmptyFields}
            showGroupTitles={renderConfig.showGroupTitles}
            showGroupDescriptions={renderConfig.showGroupDescriptions}
          />
        ))}
      </div>
    </div>
  );
};
